import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { documentRepo, lookups } from '../../data/db';
import * as Fi from 'react-icons/fi';

type IconProps = React.SVGAttributes<SVGElement>;
const FiUpload = Fi.FiUpload as React.FC<IconProps>;
const FiFileText = Fi.FiFileText as React.FC<IconProps>;
const FiX = Fi.FiX as React.FC<IconProps>;

const inputClass =
  'w-full rounded-lg border border-solid border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

const DocumentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const users = useMemo(() => lookups.users(), []);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('Contract');
  const [caseName, setCaseName] = useState('');
  const [uploadedById, setUploadedById] = useState(users[0]?.id ?? '');
  const [size, setSize] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const canSubmit = name.trim() && type.trim() && uploadedById;
  const uploadedBy = users.find((u) => u.id === uploadedById) ?? users[0];

  const handleFileSelect = useCallback((selected: File | null) => {
    setFile(selected);
    if (selected) {
      if (!name.trim()) setName(selected.name);
      setSize(formatFileSize(selected.size));
    } else {
      setSize('');
    }
  }, [name]);

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    handleFileSelect(f ?? null);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const clearFile = () => {
    setFile(null);
    setSize('');
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !uploadedBy) return;

    const url = file ? URL.createObjectURL(file) : undefined;

    const created = documentRepo.create({
      name: name.trim(),
      type: type.trim(),
      caseName: caseName.trim() || undefined,
      uploadedBy,
      size: size.trim() || undefined,
      url,
    });

    navigate(`/dashboard/documents/${created.id}`);
  };

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file && isImageFile(file)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return () => {};
  }, [file]);

  const showImagePreview = file && isImageFile(file);

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Upload a document and add metadata.</p>

      {/* File picker / drop zone */}
      <div className="mb-6">
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={`relative rounded-xl border-2 border-dashed transition-colors ${
            dragOver
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
          } ${file ? 'p-4' : 'py-10 px-6'}`}
        >
          <input
            type="file"
            id="doc-file"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={onFileInputChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
          />
          {!file ? (
            <label htmlFor="doc-file" className="cursor-pointer flex flex-col items-center justify-center gap-2 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                <FiUpload className="w-6 h-6" />
              </span>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Drop a file here or click to browse
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                PDF, Word, Excel, or images
              </span>
            </label>
          ) : (
            <div className="flex items-start gap-4">
              <div className="shrink-0">
                {showImagePreview && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt=""
                    className="h-20 w-20 rounded-lg object-cover border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                ) : isPdfFile(file) ? (
                  <div className="h-20 w-20 rounded-lg border border-slate-200 dark:border-slate-600 bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                    <FiFileText className="w-10 h-10 text-red-600 dark:text-red-400" />
                  </div>
                ) : (
                  <div className="h-20 w-20 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                    <FiFileText className="w-10 h-10 text-slate-500 dark:text-slate-400" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate" title={file.name}>
                  {file.name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {formatFileSize(file.size)} · {file.type || 'Unknown type'}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); clearFile(); }}
                className="shrink-0 p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Remove file"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FormField label="File name" hint="Metadata: display name (can match the uploaded file)">
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Merger Agreement Draft.pdf"
            />
          </FormField>
        </div>

        <FormField label="Type">
          <input
            className={inputClass}
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="e.g., Contract"
          />
        </FormField>

        <FormField label="Case name" hint="Optional">
          <input
            className={inputClass}
            value={caseName}
            onChange={(e) => setCaseName(e.target.value)}
            placeholder="e.g., Merger Agreement Review"
          />
        </FormField>

        <FormField label="Uploaded by">
          <select
            className={inputClass}
            value={uploadedById}
            onChange={(e) => setUploadedById(e.target.value)}
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Size" hint="Optional (auto-filled from file)">
          <input
            className={inputClass}
            value={size}
            onChange={(e) => setSize(e.target.value)}
            placeholder="e.g., 2.4 MB"
          />
        </FormField>

        <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
          <Button variant="secondary" onClick={() => navigate(-1)} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            Create
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DocumentCreatePage;
